'use client'

import Image from 'next/image'

import { type Spacing, getOuterSpacingClassNames } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

export interface PlatformIcon {
  icon?: string
  name?: string
}

export interface BodiAppModuleProps {
  attributes?: any
  backgroundColor?: string
  appIconImage?: string
  appIconImageAlt?: string
  ratingValue?: string
  ratingStarsImage?: string
  ratingText?: string
  phoneMockupImage?: string
  phoneMockupImageAlt?: string
  eyebrowText?: string
  heading?: string
  reviewTitle?: string
  reviewQuote?: string
  reviewSourceLogo?: string
  reviewSourceLogoAlt?: string
  reviewDecorationImage?: string
  platformIcons?: PlatformIcon[]
  outerSpacing?: Spacing
}

const MAX_STARS = 5

const DEFAULT_IMAGES = {
  appIcon: '/images/bodi_app_icon.svg',
  stars: '/images/star.svg',
  mockup: '/images/bodi_app_mockup.png',
  sourceLogo: '/images/cnn-app-review.svg',
  decoration: '/images/bodi_app_review_decoration.svg',
  glow: '/images/bodi_app_module_glow.png',
} as const

const DEFAULT_PLATFORM_ICONS: PlatformIcon[] = [
  { icon: '/images/iOS.svg', name: 'iOS' },
  { icon: '/images/android.svg', name: 'Android' },
  { icon: '/images/roku.svg', name: 'Roku' },
  { icon: '/images/apple-tv.svg', name: 'Apple TV' },
  { icon: '/images/fire-tv.svg', name: 'Fire TV' },
  { icon: '/images/casting.svg', name: 'Casting' },
]

function resolveImage(value: string | undefined, fallback: string): string {
  return value?.trim() ?? fallback
}

export function BodiAppModule({
  attributes,
  backgroundColor = '#161819',
  appIconImage,
  appIconImageAlt = 'BODi App',
  ratingValue = '4.9',
  ratingStarsImage,
  ratingText = '280,000+ App Store Reviews',
  phoneMockupImage,
  phoneMockupImageAlt = 'BODi App on mobile devices',
  eyebrowText = 'THE BODi APP',
  heading = 'Your gym. Anytime, anywhere',
  reviewTitle = 'Best Tested Workout App',
  reviewQuote = '\u201C...An enormous variety of workout programs\u201D \u201Chigh-quality instruction\u201D \u201Cmultitude of fitness styles.\u201D',
  reviewSourceLogo,
  reviewSourceLogoAlt = 'Review source',
  reviewDecorationImage,
  platformIcons = DEFAULT_PLATFORM_ICONS,
  outerSpacing,
}: BodiAppModuleProps) {
  const resolvedAppIcon = resolveImage(appIconImage, DEFAULT_IMAGES.appIcon)
  const resolvedStars = resolveImage(ratingStarsImage, DEFAULT_IMAGES.stars)
  const resolvedMockup = resolveImage(phoneMockupImage, DEFAULT_IMAGES.mockup)
  const resolvedSourceLogo = resolveImage(reviewSourceLogo, DEFAULT_IMAGES.sourceLogo)
  const resolvedDecoration = resolveImage(reviewDecorationImage, DEFAULT_IMAGES.decoration)
  const rating = Math.min(parseFloat(ratingValue) || 0, MAX_STARS)

  return (
    <section
      data-testid={attributes?.['data-testid'] ?? 'bodi-app-module'}
      builder-id={attributes?.['builder-id']}
      className={cn(
        'relative right-1/2 left-1/2 mx-[-50vw] w-screen overflow-hidden border-t border-bodi-gray-dark lg:h-[480px]',
        getOuterSpacingClassNames(outerSpacing),
        attributes?.className
      )}
      style={{ backgroundColor, ...attributes?.style }}
    >
      <div className="relative mx-auto flex w-full max-w-[1480px] flex-col px-6 lg:h-[480px] lg:flex-row lg:items-center">
        <div className="relative flex items-center gap-4 overflow-visible px-8 py-4 lg:w-1/2 lg:items-stretch lg:justify-center lg:self-stretch lg:px-0 lg:py-0">
          <Image
            src={DEFAULT_IMAGES.glow}
            alt=""
            width={790}
            height={480}
            className="pointer-events-none absolute bottom-0 z-0 h-[280px] w-[490px] lg:bottom-[-50px] lg:h-[480px] lg:w-[790px]"
            aria-hidden="true"
          />

          <div
            data-testid="bodi-app-module-rating"
            className="z-10 flex w-[90px] shrink-0 flex-col items-center justify-center gap-4 lg:w-[160px] lg:gap-8"
          >
            <Image
              data-testid="bodi-app-module-icon"
              src={resolvedAppIcon}
              alt={appIconImageAlt}
              width={80}
              height={80}
              className="size-10 rounded-[10px] lg:size-20 lg:rounded-[20px]"
            />
            <div className="flex flex-col items-center gap-4 lg:gap-8">
              <div className="flex flex-col items-center">
                <p className="font-lateral text-[30px] leading-[26px] font-bold tracking-[0.06px] text-white lg:text-[60px] lg:leading-[52px] lg:tracking-[0.12px]">
                  {ratingValue}
                </p>
                <div
                  data-testid="bodi-app-module-stars"
                  className="mt-1 flex gap-[2px] lg:mt-2 lg:gap-1"
                  role="img"
                  aria-label={`${ratingValue} out of ${MAX_STARS} stars`}
                >
                  {Array.from({ length: MAX_STARS }, (_, i) => {
                    const fillPercent = Math.max(0, Math.min(1, rating - i))

                    return (
                      <div key={i} className="relative size-[10px] lg:size-5">
                        <Image
                          src={resolvedStars}
                          alt=""
                          width={20}
                          height={20}
                          className="absolute inset-0 size-full opacity-25"
                          aria-hidden="true"
                        />
                        {fillPercent > 0 && (
                          <div
                            className="absolute inset-0 overflow-hidden"
                            style={{ width: `${fillPercent * 100}%` }}
                          >
                            <Image
                              src={resolvedStars}
                              alt=""
                              width={20}
                              height={20}
                              className="size-[10px] min-w-[10px] lg:size-5 lg:min-w-5"
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              <p
                data-testid="bodi-app-module-rating-text"
                className="text-center text-[8px] leading-[18px] font-normal tracking-[0.016px] text-white lg:text-body-base"
              >
                {ratingText}
              </p>
            </div>
          </div>

          <div
            data-testid="bodi-app-module-phones"
            className="relative z-[1] mb-[-20px] flex flex-1 items-end pr-2 lg:mb-0 lg:max-w-[60%] lg:flex-none lg:self-end lg:pr-0"
          >
            <Image
              data-testid="bodi-app-module-phones-img"
              src={resolvedMockup}
              width={377}
              height={433}
              alt={phoneMockupImageAlt}
              className="relative z-[1] h-[211px] lg:h-full lg:max-h-[433px]"
            />
            <div
              className="absolute inset-x-0 bottom-0 z-[2] h-[85px] lg:hidden"
              style={{
                background: `linear-gradient(to top, ${backgroundColor}, transparent)`,
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        <div
          data-testid="bodi-app-module-content"
          className="m-auto flex max-w-[450px] flex-col gap-8 pt-6 pb-8 lg:w-1/2 lg:max-w-[556px] lg:shrink-0 lg:items-center lg:justify-center lg:px-0 lg:py-0"
        >
          <div className="flex w-full flex-col gap-1">
            <p
              data-testid="bodi-app-module-eyebrow"
              className="text-body-base leading-[12px] font-semibold tracking-[0.002em] text-bodi-accent-bright-500 lg:text-center lg:leading-[18px]"
            >
              {eyebrowText}
            </p>
            <h2
              data-testid="bodi-app-module-heading"
              className="text-heading-2 w-full font-lateral text-white lg:text-center"
            >
              {heading}
            </h2>
          </div>

          {(reviewTitle || reviewQuote) && (
            <div
              data-testid="bodi-app-module-review"
              className="flex w-full items-center lg:w-[406px]"
            >
              <Image
                src={resolvedDecoration}
                width={33}
                height={68}
                alt=""
                className="h-[68px] w-[33px] shrink-0"
                aria-hidden="true"
              />
              <div className="flex flex-1 flex-col items-center gap-2">
                {reviewTitle && (
                  <p className="text-center text-body-sm font-normal text-white italic">
                    {reviewTitle}
                  </p>
                )}
                {reviewQuote && (
                  <p className="text-center text-body-sm font-normal text-white">{reviewQuote}</p>
                )}
                {resolvedSourceLogo && (
                  <Image
                    data-testid="bodi-app-module-review-logo"
                    src={resolvedSourceLogo}
                    alt={reviewSourceLogoAlt}
                    width={111}
                    height={23}
                    className="mt-1 h-[23px] w-auto"
                  />
                )}
              </div>
              <Image
                src={resolvedDecoration}
                width={33}
                height={68}
                alt=""
                className="h-[68px] w-[33px] shrink-0 -scale-x-100"
                aria-hidden="true"
              />
            </div>
          )}

          {platformIcons.length > 0 && (
            <div
              data-testid="bodi-app-module-platforms"
              className="flex items-center justify-center gap-4 lg:gap-6"
            >
              {platformIcons.map((platform, index) => (
                <Image
                  key={index}
                  src={platform.icon ?? ''}
                  alt={platform.name ?? ''}
                  width={45}
                  height={22}
                  className="h-4 w-auto lg:h-[22px]"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
